import { useState } from 'react';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useGetMyProfile } from '@/api';
import { JellyIcon, TshirtIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { CLASSROOM_ROUTES } from '@/shared/constants/routes';
import { AvatarDecorateModal, useMyPoints, useProfileAvatarSave, useTrackedAvatarSave } from '@/shared/features/avatar-decorate';
import { FullBodyAvatar } from '@/shared/features/user-avatar';
import { Button } from '@/ui_v3';

/** 포인트 칩 목적지 — 내 포인트(적립·사용 내역) 페이지. */
const MY_POINTS_HREF = '/profile/points';

/**
 * 시트 미등록 문구. 등록 후 defaultValue 제거.
 * 링크 본문이 잔액 숫자뿐이라 스크린리더가 "10" 만 읽는다 — 목적지를 aria-label 로 보완한다.
 */
const MY_POINTS_LABEL = {
  key: 'common:avatarDecorate.goToMyPoints',
  defaultValue: '내 젤리 보기'
} as const;

/**
 * /profile/edit 상단 아바타 영역 (Figma 605:12012).
 * 폼 카드 최상단에 위치하는 그라데이션 박스 + 전신 아바타(60×80) + "아바타 꾸미기" 버튼.
 */
export const ProfileAvatarCard = () => {
  const {
    t
  } = useTranslation('common');
  const {
    data: myProfile
  } = useGetMyProfile();
  const [isModalOpen, setModalOpen] = useState(false);
  const {
    save,
    isSaving
  } = useProfileAvatarSave();
  const {
    user
  } = useUser();
  // 포인트는 학생 계정에만 노출한다(선생님·일반·기업은 포인트 UI 미노출).
  const isStudent = user?.type?.toLowerCase() === 'student';
  const {
    data: myPoints
  } = useMyPoints();
  const avatarData = myProfile?.data.avatarData ?? null;
  const trackedSave = useTrackedAvatarSave(save, 'profile_edit', avatarData);
  const handleOpen = () => {
    logger.track('open_avatar_decorate_modal', {
      from: 'profileEdit'
    });
    setModalOpen(true);
  };
  return <>
      <div className="relative flex w-full flex-col items-center gap-md rounded-md bg-gradient-to-b from-[#f4f7ff] from-[70%] to-white px-xl pb-none pt-lg">
        {isStudent &&
      // Figma 1187:33858 — 우상단 16/16, Button/outlined/neutral md·full,
      // bg white + shadow/xs.
      // 표시 전용이었으나(pointer-events-none) "눌러야 될 것 같은데 아무 동작도 안 됨"
      // 제보가 있어 '내 포인트'로 가는 링크로 바꿨다. 잔액을 본 직후 내역이 궁금한
      // 자리라 목적지가 자연스럽다.
      <Button variant="outlined" intent="neutral" size="md" rounded="full" asChild className="absolute right-md top-md bg-white shadow-xs hover:!bg-white hover:shadow-sm">
            {/*
              asChild 일 때 Button 은 children 만 렌더하고 leftSlot 을 버린다(타입도 never).
              코인 아이콘은 Link 안에 직접 둔다 — Slot 이 버튼 클래스(gap 포함)를 Link 로
              넘기므로 leftSlot 을 쓸 때와 같은 배치가 된다.
             */}
            <Link href={MY_POINTS_HREF} aria-label={t(MY_POINTS_LABEL.key, {
          defaultValue: MY_POINTS_LABEL.defaultValue
        })}>
              <JellyIcon size="sm" />
              {myPoints?.currentBalance ?? 0}
            </Link>
          </Button>}
        <FullBodyAvatar avatarData={avatarData ?? {}} pixelHeight={80} data-sentry-element="FullBodyAvatar" data-sentry-source-file="ProfileAvatarCard.tsx" />
        <Button variant="contained" intent="strong" size="sm" rounded="full" leftSlot={<TshirtIcon size="xs" color="#fff" />} onClick={handleOpen} data-sentry-element="Button" data-sentry-source-file="ProfileAvatarCard.tsx">
          {t('avatarDecorate.openButton')}
        </Button>
      </div>
      {isModalOpen && <AvatarDecorateModal initialAvatar={avatarData} onSave={trackedSave} source="profile_edit" isSaving={isSaving} insufficientPointHelpHref={CLASSROOM_ROUTES.list()} onClose={() => setModalOpen(false)} />}
    </>;
};